import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  onErrorNetwork,
  resetErrorNetwork,
} from "../utils/networkStatusBus";
import { PageRoutes } from "../constants/routes";

const REDIRECT_DELAY_MS = 500;

const SKIP_REDIRECT_PATH_PREFIXES = [PageRoutes.GamePlay];

const shouldSkipRedirect = (pathname: string): boolean =>
  SKIP_REDIRECT_PATH_PREFIXES.some((p) => pathname.startsWith(`${p}/`));

export const useErrorNetworkRedirect = (): void => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    const handle = () => {
      if (location.pathname === PageRoutes.ErrorNetwork) return;
      if (shouldSkipRedirect(location.pathname)) {
        resetErrorNetwork();
        return;
      }

      const from = location.pathname + location.search;
      const fromState = location.state;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        // Only show the error page when the app is actually in the foreground
        // and the device reports itself offline. If the app is backgrounded
        // (hidden), defer — the network-status bus re-notifies on return to the
        // foreground. If the device is online, the disconnect was transient
        // (e.g. a request dropped while suspended), so clear the pending state
        // instead of navigating.
        if (document.visibilityState !== "visible") return;
        if (navigator.onLine !== false) {
          resetErrorNetwork();
          return;
        }
        navigate(PageRoutes.ErrorNetwork, {
          replace: true,
          state: { from, fromState },
        });
      }, REDIRECT_DELAY_MS);
    };

    const unsubscribe = onErrorNetwork(handle);

    return () => {
      unsubscribe();
      if (timer) clearTimeout(timer);
    };
  }, [navigate, location.pathname, location.search, location.state]);
};
